/**
 * Stack spread animation composable
 * Creates staggered reveal animation for stacked elements using IntersectionObserver
 * Respects user's reduced motion preference
 */

import { ref, onUnmounted} from 'vue'

/** Stack spread animation options */
export interface StackSpreadOptions {
  threshold?: number
  rootMargin?: string
  animationClass?: string
  visibleClass?: string
  staggerDelay?: number
  totalElements?: number
}

/** Return type of useStackSpreadAnimation */
export interface UseStackSpreadAnimationReturn {
  /** Register element for animation */
  registerElement: (el: HTMLElement | null | undefined, index: number) => void
  /** Check if user prefers reduced motion */
  prefersReducedMotion: () => boolean
}

/**
 * Stack spread animation with IntersectionObserver
 * Animates stacked elements with stagger delay when they enter viewport
 * 
 * @param options - Animation configuration options
 * @returns Element registration function
 * 
 * @example
 * const { registerElement } = useStackSpreadAnimation({ totalElements: 3 })
 * <div :ref="(el) => registerElement(el, 0)">Card 1</div>
 */
export function useStackSpreadAnimation(
  options: StackSpreadOptions = {}
): UseStackSpreadAnimationReturn {
  const {
    threshold = 0.1,
    rootMargin = '0px',
    animationClass = 'stack-spread',
    visibleClass = 'is-spread',
    staggerDelay = 150,
    totalElements = 3,
  } = options

  const elements = ref<(HTMLElement | undefined)[]>([])
  const observer = ref<IntersectionObserver | null>(null)
  const initialized = ref<boolean>(false)
  const timeouts = ref<ReturnType<typeof setTimeout>[]>([])

  /**
   * Check if user prefers reduced motion
   * @returns True if reduced motion is preferred
   */
  const prefersReducedMotion = (): boolean => {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  }

  /**
   * Initialize IntersectionObserver for animation
   * @private
   */
  const initObserver = (): void => {
    if (elements.value.length === 0) {
      return
    }

    // Skip animation if user prefers reduced motion
    if (prefersReducedMotion()) {
      elements.value.forEach(el => {
        if (el) {
          el.classList.add(visibleClass)
          el.classList.add('no-animation')
        }
      })
      return
    }

    observer.value = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            elements.value.forEach((el, index) => {
              if (el) {
                const timeoutId = setTimeout(() => {
                  el.classList.add(visibleClass)
                }, index * staggerDelay)
                timeouts.value.push(timeoutId)
              }
            })
            observer.value?.disconnect()
          }
        })
      },
      {
        threshold,
        rootMargin,
      }
    )

    // Observe the parent container
    const parent = elements.value[0]?.parentElement
    if (parent) {
      observer.value.observe(parent)
    }
  }

  /**
   * Register element for animation
   * @param el - HTML element to animate
   * @param index - Element position in stack (for stagger timing)
   */
  const registerElement = (el: HTMLElement | null | undefined, index: number): void => {
    if (el && !elements.value.includes(el)) {
      elements.value[index] = el
      el.classList.add(animationClass)
      el.setAttribute('data-position', String(index))

      // Check if all elements are registered
      if (elements.value.filter(Boolean).length === totalElements && !initialized.value) {
        initialized.value = true

        // Init observer after small delay
        setTimeout(initObserver, 50)
      }
    }
  }

  /**
   * Cleanup observer and timeouts
   * @private
   */
  const cleanup = (): void => {
    if (observer.value) {
      observer.value.disconnect()
    }
    timeouts.value.forEach(clearTimeout)
    timeouts.value = []
  }

  onUnmounted(() => {
    cleanup()
  })

  return {
    registerElement,
    prefersReducedMotion,
  }
}